import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import useIssuedManualSearch from 'views/App/Portal/Policy/SearchManuals/_data_source/useIssuedManualSearch';
import useDraftManualSearch from 'views/App/Portal/Policy/SearchManuals/_data_source/useDraftManualSearch';
import { getIssuedManualContentPdf } from '../IssuedManuals/_data_sources/useIssuedManualContentPdf';
import contentHelper from 'helpers/contentHelper';
import _ from 'lodash';
import useStickyState from 'hooks/useStickyState';
import { ManualSearchResult } from '../_types';
import { UseInfiniteQueryResult } from 'react-query';
import { ApiCollectionResponse } from 'types/api_response';

type SearchType = 'issued' | 'draft';

interface SearchContextType {
  searchType: SearchType;
  setSearchType: (type: SearchType) => void;
  searchQuery: UseInfiniteQueryResult<ApiCollectionResponse<ManualSearchResult>, Error>;
  searchResults: any;
  searchManuals: (query: string) => void;
  loading: boolean;
  handleDownload: (manualId: string, itemId: string, type: 'POLICY' | 'PREFACE', title?: string) => Promise<void>;
  selectedItem: any;
  setSelectedItem: (item: any) => void;
  isContentLoading: boolean;
  setIsContentLoading: (value: boolean) => void;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  getHighlightedText: (highlights: ManualSearchResult['highlights'], maxLength?: number) => string;
  searchResultsCount: number;
  isSearchOpen: boolean;
  setIsSearchOpen: (value: boolean) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

/** Bad values stored when useStickyState args were reversed (key stored as value). Treat as empty. */
const BAD_SEARCH_INPUT_VALUES = ['search_manual_issued', 'search_manual_draft'];

const normalizeSearchInputValue = (value: string): string =>
  BAD_SEARCH_INPUT_VALUES.includes(value) ? '' : value;

const stripHtmlTags = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchType, setSearchType] = useStickyState<SearchType>('search_manual_type', 'issued' as SearchType);

  // Ensure searchType is always a valid value
  const validSearchType = searchType === 'issued' || searchType === 'draft' ? searchType : 'issued';

  const [searchInputValue, setSearchInputValue] = useStickyState(`search_manual_${validSearchType}`, '');

  const effectiveSearchInputValue = normalizeSearchInputValue(searchInputValue);

  // Correct bad values that were stored when useStickyState args were reversed (key stored as value).
  // Remove legacy bad entries from localStorage before resetting state.
  useEffect(() => {
    if (BAD_SEARCH_INPUT_VALUES.includes(searchInputValue)) {
      BAD_SEARCH_INPUT_VALUES.forEach((key) => localStorage.removeItem(key));
      setSearchInputValue('');
    }
  }, [searchInputValue, setSearchInputValue]);

  // Update search input key when search type changes
  useEffect(() => {
    // Only load from localStorage if we don't have a current search input value
    // This preserves the search input when switching tabs
    if (!searchInputValue || searchInputValue.trim() === '') {
      const storedValue = localStorage.getItem(`search_manual_${validSearchType}`);
      if (storedValue) {
        try {
          const parsed = JSON.parse(storedValue) as string;
          setSearchInputValue(normalizeSearchInputValue(parsed));
        } catch {
          setSearchInputValue('');
        }
      } else {
        setSearchInputValue('');
      }
    }
  }, [validSearchType, setSearchInputValue, searchInputValue]);

  // Use the appropriate search hook based on search type (use normalized value so API never sees bad value)
  const issuedSearchQuery = useIssuedManualSearch(
    effectiveSearchInputValue,
    {},
    {
      enabled:
        isSearchOpen &&
        effectiveSearchInputValue !== null &&
        effectiveSearchInputValue !== undefined &&
        validSearchType === 'issued',
    },
  );

  const draftSearchQuery = useDraftManualSearch(
    effectiveSearchInputValue,
    {},
    {
      enabled:
        isSearchOpen &&
        effectiveSearchInputValue !== null &&
        effectiveSearchInputValue !== undefined &&
        validSearchType === 'draft',
    },
  );

  // Use the active search query based on search type
  const searchQuery = validSearchType === 'issued' ? issuedSearchQuery : draftSearchQuery;

  const getHighlightedText = useCallback(
    (highlights: ManualSearchResult['highlights'], maxLength: number = 70): string => {
      if (!highlights?.texts?.length) return '';

      const hitIndex = highlights.texts.findIndex((text) => text.type === 'hit');
      if (hitIndex === -1) return '';

      const truncateAtWordBoundary = (text: string, maxLength: number, fromEnd: boolean = false): string => {
        if (text.length <= maxLength) return text;

        if (fromEnd) {
          const truncated = text.substring(text.length - maxLength);
          const firstSpace = truncated.indexOf(' ');
          return firstSpace > 0 ? '...' + truncated.substring(firstSpace + 1) : '...' + truncated;
        } else {
          const truncated = text.substring(0, maxLength);
          const lastSpace = truncated.lastIndexOf(' ');
          return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
        }
      };

      const hitText = highlights.texts[hitIndex].value;
      const hitTextLength = hitText.length;

      // Calculate remaining length after accounting for hit text and ellipsis
      const remainingLength = maxLength - hitTextLength - 6; // 6 for ellipsis (3) and strong tags (3)
      const perSideLength = Math.floor(remainingLength / 2);

      const beforeText = hitIndex > 0 ? stripHtmlTags(highlights.texts[hitIndex - 1].value) : '';
      const afterText =
        hitIndex < highlights.texts.length - 1 ? stripHtmlTags(highlights.texts[hitIndex + 1].value) : '';

      // Adjust truncation based on presence of before/after text and available length
      const truncatedBeforeText = beforeText
        ? truncateAtWordBoundary(beforeText, afterText ? perSideLength : remainingLength, true)
        : '';
      const truncatedAfterText = afterText
        ? truncateAtWordBoundary(afterText, beforeText ? perSideLength : remainingLength)
        : '';

      return `${truncatedBeforeText}<strong>${hitText}</strong>${truncatedAfterText}`;
    },
    [],
  );

  const handleDownload = useCallback(
    async (manualId: string, itemId: string, type: 'POLICY' | 'PREFACE', title?: string) => {
      const fileContent = await getIssuedManualContentPdf(manualId, itemId, type);
      if (fileContent) {
        const bytes = new Uint8Array(fileContent.length);
        for (let i = 0; i < fileContent.length; i++) {
          bytes[i] = fileContent.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const href = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', contentHelper.parameterize(title || 'download'));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }
    },
    [],
  );

  return (
    <SearchContext.Provider
      value={{
        searchType: validSearchType,
        setSearchType,
        searchQuery,
        searchResults: searchQuery.data?.pages.flatMap((page) => page.content) || [],
        searchManuals: (query: string) => setSearchInputValue(query),
        loading: searchQuery.isLoading || searchQuery.isFetching,
        selectedItem,
        setSelectedItem,
        handleDownload,
        isContentLoading,
        setIsContentLoading,
        searchInputValue: effectiveSearchInputValue,
        setSearchInputValue,
        getHighlightedText,
        searchResultsCount: searchQuery.data?.pages[0]?.count || 0,
        isSearchOpen,
        setIsSearchOpen,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};
